/**
 * Demo fixed routes for Advance Rostering smoke / local demos.
 * Injected into FixedRoutesContext via RosteringPanel load-demo actions.
 */
import type { FixedRoute, FixedRouteStop, ServiceDaysMask } from '../../types/fixedRoutes';

const WEEKDAYS: ServiceDaysMask = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

function everyNMinutes(startMin: number, endMin: number, step: number): number[] {
  const out: number[] = [];
  for (let m = startMin; m <= endMin; m += step) out.push(m);
  return out;
}

function makeStop(id: string, name: string, lat: number, lng: number): FixedRouteStop {
  return {
    id,
    name,
    coordinates: { lat, lng },
  };
}

function makeRoute(params: {
  id: string;
  name: string;
  shortName: string;
  color: string;
  startTime: string;
  endTime: string;
  stopA: FixedRouteStop;
  stopB: FixedRouteStop;
  tripStarts: number[];
  tripDurationMinutes: number;
}): FixedRoute {
  const { stopA, stopB, tripStarts, tripDurationMinutes } = params;
  return {
    id: params.id,
    name: params.name,
    shortName: params.shortName,
    color: params.color,
    startTime: params.startTime,
    endTime: params.endTime,
    stops: [
      { stopId: stopA.id, waitTimeMinutes: 1 },
      { stopId: stopB.id, waitTimeMinutes: 1 },
    ],
    segments: [
      {
        fromStopId: stopA.id,
        toStopId: stopB.id,
        travelTimeMinutes: Math.max(5, tripDurationMinutes - 2),
        distanceMeters: 8000,
      },
    ],
    tripSchedules: tripStarts.map((startTimeMinutes) => ({
      startTimeMinutes,
      stopArrivalTimesMinutes: [0, tripDurationMinutes],
    })),
    serviceDays: WEEKDAYS,
    isBusRoute: true,
  };
}

export const ROSTERING_DEMO_ROUTE_IDS = [
  'demo-route-a-city-loop',
  'demo-route-b-airport',
  'demo-route-c-coastal',
] as const;

/** Large demo route ids — denser network for objective comparisons. */
export const ROSTERING_LARGE_DEMO_ROUTE_IDS = [
  'demo-lg-city-early',
  'demo-lg-city-mid',
  'demo-lg-city-late',
  'demo-lg-airport-am',
  'demo-lg-airport-pm',
  'demo-lg-coastal-am',
  'demo-lg-coastal-pm',
  'demo-lg-university',
  'demo-lg-hospital',
  'demo-lg-industrial',
] as const;

/** Stable demo route → required vehicle tags (also used if React state is stale). */
export const ROSTERING_DEMO_ROUTE_TAGS: Record<string, string[]> = {
  [ROSTERING_DEMO_ROUTE_IDS[0]]: ['standard_bus'],
  [ROSTERING_DEMO_ROUTE_IDS[1]]: ['accessible'],
  [ROSTERING_DEMO_ROUTE_IDS[2]]: ['coach'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[0]]: ['standard_bus'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[1]]: ['standard_bus'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[2]]: ['standard_bus'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[3]]: ['accessible'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[4]]: ['accessible'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[5]]: ['coach'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[6]]: ['coach'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[7]]: ['standard_bus'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[8]]: ['accessible'],
  [ROSTERING_LARGE_DEMO_ROUTE_IDS[9]]: ['standard_bus'],
};

export type RosteringDemoDriverSeed = {
  display_name: string;
  /** Stable id for idempotent re-loads (matched against external_driver_id). */
  external_driver_id: string;
};

export type RosteringDemoBundle = {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  /** route_id → required capability tags for rostering snapshots */
  routeRequiredTags: Record<string, string[]>;
  /** vehicle_id → capability tags to upsert for tag-matching demos */
  vehicleTags: Array<{ vehicle_id: string; capability_tags: string[] }>;
  /** Drivers to ensure exist before propose (idempotent via external_driver_id). */
  drivers: RosteringDemoDriverSeed[];
};

/**
 * Builds three weekday demo routes with trip schedules.
 * Times are local HH:mm; trip starts are minutes from midnight.
 */
export function buildRosteringDemoBundle(): RosteringDemoBundle {
  const stops = [
    makeStop('demo-stop-city-a', 'City Centre', -33.8688, 151.2093),
    makeStop('demo-stop-city-b', 'Harbour', -33.858, 151.214),
    makeStop('demo-stop-airport-a', 'Central Station', -33.883, 151.206),
    makeStop('demo-stop-airport-b', 'Airport T1', -33.9399, 151.1753),
    makeStop('demo-stop-coast-a', 'Beach Hub', -33.8915, 151.2767),
    makeStop('demo-stop-coast-b', 'Coastal Pier', -33.85, 151.28),
  ];

  const [cityA, cityB, airA, airB, coastA, coastB] = stops;

  const routes: FixedRoute[] = [
    makeRoute({
      id: ROSTERING_DEMO_ROUTE_IDS[0],
      name: 'Route A City Loop',
      shortName: 'A',
      color: '#1976d2',
      startTime: '06:00',
      endTime: '14:00',
      stopA: cityA,
      stopB: cityB,
      tripStarts: [
        ...everyNMinutes(6 * 60, 11 * 60, 30),
        ...everyNMinutes(13 * 60, 14 * 60 - 30, 30),
      ],
      tripDurationMinutes: 40,
    }),
    makeRoute({
      id: ROSTERING_DEMO_ROUTE_IDS[1],
      name: 'Route B Airport Express',
      shortName: 'B',
      color: '#388e3c',
      startTime: '08:00',
      endTime: '18:00',
      stopA: airA,
      stopB: airB,
      tripStarts: [
        ...everyNMinutes(8 * 60, 12 * 60, 40),
        ...everyNMinutes(14 * 60, 17 * 60, 40),
      ],
      tripDurationMinutes: 55,
    }),
    makeRoute({
      id: ROSTERING_DEMO_ROUTE_IDS[2],
      name: 'Route C Coastal',
      shortName: 'C',
      color: '#f57c00',
      startTime: '09:00',
      endTime: '17:00',
      stopA: coastA,
      stopB: coastB,
      tripStarts: everyNMinutes(9 * 60, 16 * 60, 45),
      tripDurationMinutes: 50,
    }),
  ];

  return {
    stops,
    routes,
    routeRequiredTags: Object.fromEntries(
      ROSTERING_DEMO_ROUTE_IDS.map((id) => [id, ROSTERING_DEMO_ROUTE_TAGS[id]])
    ),
    vehicleTags: [
      { vehicle_id: 'bus-1', capability_tags: ['standard_bus'] },
      { vehicle_id: 'bus-2', capability_tags: ['standard_bus'] },
      { vehicle_id: 'bus-3', capability_tags: ['accessible'] },
      { vehicle_id: 'bus-4', capability_tags: ['accessible'] },
      { vehicle_id: 'coach-1', capability_tags: ['coach'] },
    ],
    drivers: [
      { display_name: 'Demo Driver 1', external_driver_id: 'demo-driver-01' },
      { display_name: 'Demo Driver 2', external_driver_id: 'demo-driver-02' },
      { display_name: 'Demo Driver 3', external_driver_id: 'demo-driver-03' },
      { display_name: 'Demo Driver 4', external_driver_id: 'demo-driver-04' },
    ],
  };
}

/**
 * Builds a denser weekday network + larger vehicle/driver pool so changing
 * the rostering objective (balanced / min vehicles / min drivers) produces
 * visibly different unique-vehicle and unique-driver stats.
 */
export function buildRosteringLargeDemoBundle(): RosteringDemoBundle {
  const stops = [
    makeStop('demo-lg-stop-city-a', 'City Hub', -33.8688, 151.2093),
    makeStop('demo-lg-stop-city-b', 'Inner West', -33.884, 151.154),
    makeStop('demo-lg-stop-air-a', 'Central', -33.883, 151.206),
    makeStop('demo-lg-stop-air-b', 'Airport', -33.9399, 151.1753),
    makeStop('demo-lg-stop-coast-a', 'Bondi Hub', -33.8915, 151.2767),
    makeStop('demo-lg-stop-coast-b', 'Manly Wharf', -33.8, 151.284),
    makeStop('demo-lg-stop-uni-a', 'Uni Campus', -33.888, 151.187),
    makeStop('demo-lg-stop-uni-b', 'Tech Park', -33.796, 151.184),
    makeStop('demo-lg-stop-hosp-a', 'General Hospital', -33.877, 151.222),
    makeStop('demo-lg-stop-hosp-b', 'Medical Precinct', -33.89, 151.24),
    makeStop('demo-lg-stop-ind-a', 'Port Gate', -33.845, 151.19),
    makeStop('demo-lg-stop-ind-b', 'Logistics Park', -33.92, 151.2),
  ];

  const [
    cityA,
    cityB,
    airA,
    airB,
    coastA,
    coastB,
    uniA,
    uniB,
    hospA,
    hospB,
    indA,
    indB,
  ] = stops;

  const ids = ROSTERING_LARGE_DEMO_ROUTE_IDS;

  const routes: FixedRoute[] = [
    makeRoute({
      id: ids[0],
      name: 'LG City Early',
      shortName: 'C1',
      color: '#1565c0',
      startTime: '05:30',
      endTime: '11:00',
      stopA: cityA,
      stopB: cityB,
      tripStarts: everyNMinutes(5 * 60 + 30, 10 * 60 + 30, 25),
      tripDurationMinutes: 35,
    }),
    makeRoute({
      id: ids[1],
      name: 'LG City Mid',
      shortName: 'C2',
      color: '#1e88e5',
      startTime: '09:00',
      endTime: '15:00',
      stopA: cityA,
      stopB: cityB,
      tripStarts: everyNMinutes(9 * 60, 14 * 60 + 30, 30),
      tripDurationMinutes: 40,
    }),
    makeRoute({
      id: ids[2],
      name: 'LG City Late',
      shortName: 'C3',
      color: '#42a5f5',
      startTime: '13:00',
      endTime: '21:00',
      stopA: cityA,
      stopB: cityB,
      tripStarts: everyNMinutes(13 * 60, 20 * 60, 30),
      tripDurationMinutes: 40,
    }),
    makeRoute({
      id: ids[3],
      name: 'LG Airport AM',
      shortName: 'A1',
      color: '#2e7d32',
      startTime: '06:00',
      endTime: '13:00',
      stopA: airA,
      stopB: airB,
      tripStarts: everyNMinutes(6 * 60, 12 * 60, 35),
      tripDurationMinutes: 50,
    }),
    makeRoute({
      id: ids[4],
      name: 'LG Airport PM',
      shortName: 'A2',
      color: '#66bb6a',
      startTime: '12:00',
      endTime: '20:00',
      stopA: airA,
      stopB: airB,
      tripStarts: everyNMinutes(12 * 60, 19 * 60, 35),
      tripDurationMinutes: 50,
    }),
    makeRoute({
      id: ids[5],
      name: 'LG Coastal AM',
      shortName: 'S1',
      color: '#ef6c00',
      startTime: '07:00',
      endTime: '13:00',
      stopA: coastA,
      stopB: coastB,
      tripStarts: everyNMinutes(7 * 60, 12 * 60, 40),
      tripDurationMinutes: 55,
    }),
    makeRoute({
      id: ids[6],
      name: 'LG Coastal PM',
      shortName: 'S2',
      color: '#ffa726',
      startTime: '12:30',
      endTime: '19:00',
      stopA: coastA,
      stopB: coastB,
      tripStarts: everyNMinutes(12 * 60 + 30, 18 * 60, 40),
      tripDurationMinutes: 55,
    }),
    makeRoute({
      id: ids[7],
      name: 'LG University Shuttle',
      shortName: 'U1',
      color: '#6a1b9a',
      startTime: '07:30',
      endTime: '18:00',
      stopA: uniA,
      stopB: uniB,
      // Lunch gap creates two packs → more demand blocks for reuse demos
      tripStarts: [
        ...everyNMinutes(7 * 60 + 30, 11 * 60 + 30, 30),
        ...everyNMinutes(13 * 60 + 30, 17 * 60 + 30, 30),
      ],
      tripDurationMinutes: 45,
    }),
    makeRoute({
      id: ids[8],
      name: 'LG Hospital Link',
      shortName: 'H1',
      color: '#c62828',
      startTime: '06:30',
      endTime: '22:00',
      stopA: hospA,
      stopB: hospB,
      tripStarts: everyNMinutes(6 * 60 + 30, 21 * 60, 45),
      tripDurationMinutes: 40,
    }),
    makeRoute({
      id: ids[9],
      name: 'LG Industrial',
      shortName: 'I1',
      color: '#455a64',
      startTime: '05:00',
      endTime: '14:00',
      stopA: indA,
      stopB: indB,
      tripStarts: everyNMinutes(5 * 60, 13 * 60, 40),
      tripDurationMinutes: 50,
    }),
  ];

  const vehicleTags: RosteringDemoBundle['vehicleTags'] = [
    ...[1, 2, 3, 4, 5, 6].map((n) => ({
      vehicle_id: `lg-bus-${n}`,
      capability_tags: ['standard_bus'],
    })),
    ...[1, 2, 3, 4].map((n) => ({
      vehicle_id: `lg-access-${n}`,
      capability_tags: ['accessible'],
    })),
    ...[1, 2, 3].map((n) => ({
      vehicle_id: `lg-coach-${n}`,
      capability_tags: ['coach'],
    })),
  ];

  const driverNames = [
    'Alex Rivera',
    'Blake Chen',
    'Casey Morgan',
    'Dana Okonkwo',
    'Ellis Park',
    'Frankie Nguyen',
    'Gray Patel',
    'Harper Singh',
    'Indigo Brooks',
    'Jordan Lee',
    'Kai Fernandez',
    'Logan Wright',
    'Morgan Ellis',
    'Noor Haddad',
  ];

  const drivers: RosteringDemoDriverSeed[] = driverNames.map((display_name, i) => ({
    display_name,
    external_driver_id: `demo-lg-driver-${String(i + 1).padStart(2, '0')}`,
  }));

  return {
    stops,
    routes,
    routeRequiredTags: Object.fromEntries(
      ROSTERING_LARGE_DEMO_ROUTE_IDS.map((id) => [id, ROSTERING_DEMO_ROUTE_TAGS[id]])
    ),
    vehicleTags,
    drivers,
  };
}
